import { Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
export const HARNESS_REVIEW_MAX_BYTES = 2 * 1024 * 1024;
const object = (properties) => Type.Object(properties, { additionalProperties: false });
const text = (maxLength = 12000) => Type.String({ minLength: 1, maxLength });
const texts = (maxItems = 100) => Type.Array(text(), { maxItems });
const id = Type.String({ pattern: "^[A-Za-z0-9][A-Za-z0-9_.:-]{0,159}$" });
const ids = Type.Array(id, { maxItems: 100, uniqueItems: true });
const decimal = Type.String({ pattern: "^\\d{1,12}(?:\\.\\d{1,4})?$" });
const nullableDecimal = Type.Union([decimal, Type.Null()]);
const tokens = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const usage = object({ inputTokens: tokens, outputTokens: tokens, cachedInputTokens: tokens });
const center = Type.Tuple([
    Type.Number({ minimum: -180, maximum: 180 }),
    Type.Number({ minimum: -90, maximum: 90 })
]);
const line = object({
    id,
    description: text(),
    unit: text(40),
    quantity: nullableDecimal,
    unitPriceEur: nullableDecimal,
    amountEur: nullableDecimal,
    quantityReason: text(),
    priceReason: text(),
    evidenceIds: ids,
    status: Type.Union([
        Type.Literal("priced"),
        Type.Literal("included"),
        Type.Literal("unresolved")
    ]),
    includedInLineId: Type.Union([id, Type.Null()])
});
const source = object({
    id,
    title: text(500),
    excerpt: text(32000),
    url: Type.Union([text(2048), Type.Null()]),
    status: Type.Union([
        Type.Literal("recorded"),
        Type.Literal("partial"),
        Type.Literal("unavailable")
    ]),
    limitations: texts()
});
const variant = object({
    id,
    label: text(500),
    summary: text(),
    observations: Type.Array(object({ text: text(), evidenceIds: ids, limitation: text() }), {
        maxItems: 100
    }),
    decisions: Type.Array(object({ text: text(), reason: text(), evidenceIds: ids, unresolved: texts() }), { maxItems: 100 }),
    lines: Type.Array(line, { minItems: 1, maxItems: 100 }),
    knownSubtotalEur: decimal,
    completeTotalEur: Type.Null(),
    unresolved: texts(200),
    sources: Type.Array(source, { minItems: 1, maxItems: 100 }),
    usage,
    review: object({
        score: Type.Union([Type.Number({ minimum: 0, maximum: 1000 }), Type.Null()]),
        maxScore: Type.Union([Type.Number({ exclusiveMinimum: 0, maximum: 1000 }), Type.Null()]),
        limitations: texts()
    })
});
/** Portable, read-only delivered results. No prompts, reasoning traces or executable tools. */
export const harnessReviewPacketSchema = object({
    schemaVersion: Type.Literal("harness-review/v1"),
    id,
    title: text(500),
    createdAt: text(64),
    provenance: object({
        kind: Type.Literal("recorded_codex_run"),
        modelRequested: text(200),
        modelActual: Type.Union([text(200), Type.Null()]),
        pricing: Type.Literal("RER provisional"),
        limitations: texts()
    }),
    usage: object({
        inputTokens: tokens,
        outputTokens: tokens,
        cachedInputTokens: tokens,
        completedCalls: tokens,
        unknownUsageCalls: tokens,
        actualCostEur: Type.Null()
    }),
    area: object({
        label: text(500),
        bbox: Type.Tuple([Type.Number(), Type.Number(), Type.Number(), Type.Number()]),
        candidateCount: tokens,
        selectionRule: text()
    }),
    template: object({
        label: text(500),
        sourceDocumentId: id,
        alternativeId: id,
        technicalSummary: text(24000),
        limitations: texts()
    }),
    cases: Type.Array(object({
        id,
        label: text(500),
        buildingId: Type.Union([
            Type.String({ pattern: "^[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$" }),
            Type.Null()
        ]),
        footprintM2: nullableDecimal,
        center: Type.Union([center, Type.Null()]),
        summary: text(),
        variants: Type.Array(variant, { minItems: 1, maxItems: 8 })
    }), { minItems: 1, maxItems: 50 })
});
function invalid(reason) {
    throw new Error(`HARNESS_REVIEW_INVALID:${reason}`);
}
function unique(values, kind) {
    if (new Set(values).size !== values.length)
        invalid(`duplicate_${kind}`);
}
function scaled(value) {
    const [whole = "0", fraction = ""] = value.split(".");
    return BigInt(whole) * 10000n + BigInt(fraction.padEnd(4, "0"));
}
function validateUsage(value) {
    if (value.cachedInputTokens > value.inputTokens)
        invalid("cached_tokens_exceed_input");
}
/** Shape, relations and exact decimal arithmetic are validated before rendering. */
export function parseHarnessReviewPacket(value) {
    let encoded;
    try {
        encoded = JSON.stringify(value);
    }
    catch {
        return invalid("not_json");
    }
    if (!encoded || new TextEncoder().encode(encoded).byteLength > HARNESS_REVIEW_MAX_BYTES)
        invalid("file_too_large");
    if (!Value.Check(harnessReviewPacketSchema, value))
        invalid("schema");
    const packet = value;
    if (!/^\d{4}-\d{2}-\d{2}T/.test(packet.createdAt) ||
        !Number.isFinite(Date.parse(packet.createdAt)))
        invalid("date");
    const [west, south, east, north] = packet.area.bbox;
    if (![west, south, east, north].every(Number.isFinite) ||
        west < -180 ||
        east > 180 ||
        south < -90 ||
        north > 90 ||
        west >= east ||
        south >= north)
        invalid("bbox");
    validateUsage(packet.usage);
    if (packet.area.candidateCount < packet.cases.length)
        invalid("candidate_count");
    unique(packet.cases.map((item) => item.id), "case");
    for (const item of packet.cases) {
        if (item.center &&
            (item.center[0] < west ||
                item.center[0] > east ||
                item.center[1] < south ||
                item.center[1] > north))
            invalid("case_outside_area");
        unique(item.variants.map((result) => result.id), "variant");
        for (const result of item.variants) {
            validateUsage(result.usage);
            unique(result.sources.map((entry) => entry.id), "source");
            unique(result.lines.map((entry) => entry.id), "line");
            const sources = new Set(result.sources.map((entry) => entry.id));
            for (const entry of [...result.lines, ...result.observations, ...result.decisions])
                if (entry.evidenceIds.some((sourceId) => !sources.has(sourceId)))
                    invalid("missing_source");
            for (const evidence of result.sources) {
                if (evidence.url !== null) {
                    let parsed;
                    try {
                        parsed = new URL(evidence.url);
                    }
                    catch {
                        return invalid("source_url");
                    }
                    if (!["http:", "https:"].includes(parsed.protocol) || parsed.username || parsed.password)
                        invalid("source_url");
                }
            }
            const lines = new Map(result.lines.map((entry) => [entry.id, entry]));
            let subtotalCents = 0n;
            for (const entry of result.lines) {
                if (entry.status === "included") {
                    if (!entry.includedInLineId ||
                        !lines.has(entry.includedInLineId) ||
                        entry.quantity !== null ||
                        entry.unitPriceEur !== null ||
                        entry.amountEur !== null)
                        invalid("included_line");
                    const seen = new Set([entry.id]);
                    let parent = entry.includedInLineId;
                    while (parent) {
                        if (seen.has(parent))
                            invalid("inclusion_cycle");
                        seen.add(parent);
                        const next = lines.get(parent);
                        if (!next)
                            invalid("missing_included_parent");
                        parent = next.includedInLineId ?? "";
                    }
                    continue;
                }
                if (entry.includedInLineId !== null)
                    invalid("unexpected_included_parent");
                if (entry.quantity === null || entry.unitPriceEur === null) {
                    if (entry.amountEur !== null || entry.status === "priced")
                        invalid("unknown_amount");
                }
                else {
                    if (entry.status !== "priced")
                        invalid("resolved_status");
                    const cents = (scaled(entry.quantity) * scaled(entry.unitPriceEur) + 500000n) / 1000000n;
                    if (entry.amountEur === null || scaled(entry.amountEur) !== cents * 100n)
                        invalid("line_amount");
                    subtotalCents += cents;
                }
            }
            if (scaled(result.knownSubtotalEur) !== subtotalCents * 100n)
                invalid("subtotal");
            const { score, maxScore } = result.review;
            if ((score === null) !== (maxScore === null) ||
                (score !== null && maxScore !== null && score > maxScore))
                invalid("review_score");
        }
    }
    return packet;
}
export function parseHarnessReviewFile(contents) {
    if (new TextEncoder().encode(contents).byteLength > HARNESS_REVIEW_MAX_BYTES)
        invalid("file_too_large");
    let value;
    try {
        value = JSON.parse(contents);
    }
    catch {
        return invalid("json");
    }
    return parseHarnessReviewPacket(value);
}
//# sourceMappingURL=harness-review.js.map